export const ESPACE_NET_URL = 'https://worldwide.espacenet.com/patent/search/?q=';
export const GOOGLE_PATENTS_URL = 'https://patents.google.com/patent/';
export const FOLDER_NAME_MAX_LENGTH = 50;

// All of these subpaths will bypass Auth0 authentication
export const OAUTH_ROUTE_PREFIX = '/oauth';

export enum PATHNAMES {
  main = '/',
  allProjects = '/all-projects',
  analytics = '/projects/analytics',
  projectFolder = '/all-projects/folder/:folderId',
  authLogin = '/auth-login',
  infringementDetection = '/infringement-detection',
  fto = '/fto',
  infringementDetectionNewSearch = '/infringement-detection/new-search',
  ftoNewSearch = '/fto/new-search',
  infringementDetectionSearch = '/infringement-detection/search',
  ftoSearch = '/fto/search',
  inventionDisclosure = '/invention-disclosure',
  orgAdminPanel = '/org-admin',
  platformAdminPanel = '/platform-admin',
  featureFlagsPanel = '/features-admin',
  developerAdminPanel = '/developer-admin',
  developerPanel = '/developer',
  apiDocs = '/api-docs',
  patentMoreInfo = '/patent',
  aiSearch = '/ai-search',
  aiSearchV2 = '/ai-search-v2',
  patentDrafting = '/drafting',
  litigationAssistant = '/litigation-assistant',
  generalAssistant = '/general-assistant',
  generalAssistantNew = '/general-assistant/new',
  inventionAssistant = '/invention-assistant',
  anticipation = '/anticipation',
  officeActionsV2Hub = '/office-actions-v2',
  officeActionsV2New = '/office-actions-v2/new',
  officeActionsV2 = '/office-actions-v2/:projectId',
  patentIntelligence = '/patent-intelligence',
  releaseNotes = '/release-notes',
  componentsDemo = '/components-demo',
  conferenceAdminPanel = '/conference-admin',
  conferenceSignup = '/conference',
  externalSharepointLogin = `${OAUTH_ROUTE_PREFIX}/sharepoint`,
}

export enum FEEDBACK_TYPE {
  PATENT_IRRELEVANT = 'PATENT_IRRELEVANT',
  CLAIMS_IRRELEVANT = 'CLAIMS_IRRELEVANT',
  CLAIMS_NOT_INFRINGED = 'CLAIMS_NOT_INFRINGED',
  FEATURES_INCORRECT = 'FEATURES_INCORRECT',
  TECHNOLOGY_IRRELEVANT = 'TECHNOLOGY_IRRELEVANT',
  COMPONENTS_IRRELEVANT = 'COMPONENTS_IRRELEVANT',
  PATENT_NOT_INCLUDED_IN_RESULTS = 'PATENT_NOT_INCLUDED_IN_RESULTS',
  SCORES_INCORRECT = 'SCORES_INCORRECT',
  PATENT_FEATURE_MISSING = 'PATENT_FEATURE_MISSING',
  RESULTS_NOT_CORRECT = 'RESULTS_NOT_CORRECT',
  OTHER = 'OTHER',
  OK = 'OK',
}

enum MODEL_PROVIDER {
  BEDROCK = 'BEDROCK',
  OPENAI = 'OPENAI',
}

export type TModelProvider = MODEL_PROVIDER | string;

export enum EXPORT_COLUMNS_ENUM {
  SEARCH_QUERY = 'SEARCH_QUERY',
  PATENT_ID = 'PATENT_ID',
  PATENT_TITLE = 'PATENT_TITLE',
  FAMILY_ID = 'FAMILY_ID',
  COUNTRY_CODE = 'COUNTRY_CODE',
  PATENT_NUMBER = 'PATENT_NUMBER',
  KIND_CODE = 'KIND_CODE',
  PUBLICATION_DATE = 'PUBLICATION_DATE',
  APPLICATION_DATE = 'APPLICATION_DATE',
}

export enum EXPORT_INVENTION_DISCLOSURE_INFRINGEMENT_DETECTION_COLUMNS_ENUM {
  PATENT_ID = 'PATENT_ID',
  PATENT_TITLE = 'PATENT_TITLE',
  FAMILY_ID = 'FAMILY_ID',
  COUNTRY_CODE = 'COUNTRY_CODE',
  PATENT_NUMBER = 'PATENT_NUMBER',
  KIND_CODE = 'KIND_CODE',
  PUBLICATION_DATE = 'PUBLICATION_DATE',
  APPLICATION_DATE = 'APPLICATION_DATE',
}

export enum InfringementDetectionStatusName {
  DRAFT = 'DRAFT',
  QUEUED = 'QUEUED',
  SEARCHING = 'SEARCHING',
  EXTRACTING_FEATURES = 'EXTRACTING_FEATURES',
  RUNNING_COMPARISON = 'RUNNING_COMPARISON',
  PREPARING_RESULTS = 'PREPARING_RESULTS',
  COMPLETE = 'COMPLETE',
  FTO_SEARCH_COMPLETE = 'FTO_SEARCH_COMPLETE',
  ERROR = 'ERROR',
}

export enum AnalysisType {
  FTO = 'FTO',
  FTO_V2 = 'FTO_V2',
  CHEMICAL_FTO = 'CHEMICAL_FTO',
  INFRINGEMENT_DETECTION = 'INFRINGEMENT_DETECTION',
}

export enum InfringementDetectionStatusInternal {
  DRAFT,
  QUEUED,
  SEARCHING,
  EXTRACTING_FEATURES,
  RUNNING_COMPARISON,
  PREPARING_RESULTS,
  COMPLETE,
  ERROR,
}
export enum FTOStatusName {
  DRAFT = 'DRAFT',
  QUEUED = 'QUEUED',
  SEARCHING = 'SEARCHING',
  FTO_SEARCH_COMPLETE = 'FTO_SEARCH_COMPLETE',
  FTO_ANALYSIS_QUEUED = 'FTO_ANALYSIS_QUEUED',
  EXTRACTING_FEATURES = 'EXTRACTING_FEATURES',
  RUNNING_COMPARISON = 'RUNNING_COMPARISON',
  PREPARING_RESULTS = 'PREPARING_RESULTS',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

export enum FTOStatusInternal {
  DRAFT,
  QUEUED,
  SEARCHING,
  FTO_SEARCH_COMPLETE,
  FTO_ANALYSIS_QUEUED,
  EXTRACTING_FEATURES,
  RUNNING_COMPARISON,
  PREPARING_RESULTS,
  COMPLETE,
  ERROR,
}

export enum FILTER_ID_OPTIONS {
  SHARED = 'shared',
}

export const TEMP_AREA_ID_STRING = 'temp-area-';

export const MAX_TREE_DEPTH = 3;

export enum MIME_TYPES {
  pdf = 'application/pdf',
  csv = 'text/csv',
  xls = 'application/vnd.ms-excel',
  xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  jpg = 'image/jpg',
  jpeg = 'image/jpeg',
  png = 'image/png',
  svg = 'image/svg+xml',
  docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc = 'application/msword',
  rtf = 'application/rtf',
  txt = 'text/plain',
  vtt = 'text/vtt',
  sdf = 'chemical/x-mdl-sdfile',
  md = 'text/markdown',
  html = 'text/html',
  step = 'model/step',
}

// File types that can be converted to PDF on the backend
const CONVERTIBLE_TO_PDF_TYPES = new Set([
  MIME_TYPES.doc,
  MIME_TYPES.rtf,
  MIME_TYPES.docx,
  MIME_TYPES.pptx,
  MIME_TYPES.xlsx,
  MIME_TYPES.txt,
  MIME_TYPES.vtt,
  MIME_TYPES.md,
  MIME_TYPES.html,
]);

export const canConvertToPdf = (mimeType: string): boolean => {
  return CONVERTIBLE_TO_PDF_TYPES.has(mimeType as MIME_TYPES);
};

// File types that don't support in-browser preview
const NON_PREVIEWABLE_TYPES = new Set([
  MIME_TYPES.doc,
  MIME_TYPES.rtf,
  MIME_TYPES.docx,
  MIME_TYPES.pptx,
  MIME_TYPES.txt,
  MIME_TYPES.vtt,
]);

// MIME types accepted when uploading a figure source file
export const FIGURE_ALLOWED_MIMES: ReadonlySet<string> = new Set([
  MIME_TYPES.pdf,
  MIME_TYPES.jpg,
  MIME_TYPES.jpeg,
  MIME_TYPES.png,
  MIME_TYPES.docx,
  MIME_TYPES.doc,
  MIME_TYPES.rtf,
  MIME_TYPES.pptx,
]);

export const supportsPreview = (fileType: string): boolean => {
  return !NON_PREVIEWABLE_TYPES.has(fileType as MIME_TYPES);
};

export enum CHEMICAL_MIME_TYPES {
  mol = 'chemical/x-mdl-molfile',
  sdf = 'chemical/x-mdl-sdfile',
  cdx = 'chemical/x-cdx',
  cdxml = 'chemical/x-cdxml',
  mrv = 'chemical/x-mrv', // marvin files have no real mime type
  smiles = 'chemical/x-smiles', // smile files have no real mime type
}

export const PATENT_REGEX = /[A-Z]{2}-[A-Z0-9]+-[A-Z]?\d?/g; // two capitalized letters, - , numbers or letters, -, 1 capitalized letter and/or 1 number

export enum ACCESS_TYPE {
  OWNER = 'OWNER',
  EDITOR = 'EDITOR',
  VIEWER = 'VIEWER',
}

export enum PATENT_DRAWER_TABS {
  SUMMARY = 'Summary',
  PATENT_SPECIFICATIONS = 'Patent Specifications',
  FIGURES = 'Figures',
  NOTES = 'Notes',
  RELEVANT_LITERATURE = 'Relevant Literature',
  ASK_AI = 'Ask AI',
}

export const AUTH_0_SCOPE = 'openid offline_access';

export const LOW_SCORE_THRESHOLD = 0.66;
export const MEDIUM_SCORE_THRESHOLD = 0.33;
type SortOption = {
  label: string;
  value: string;
};
export const sortOptions: SortOption[] = [
  { label: 'Infringement Risk: High → Low', value: 'risk_high_low' },
  { label: 'Infringement Risk: Low → High', value: 'risk_low_high' },
  { label: 'Score: Ascending', value: 'score_asc' },
  { label: 'Score: Descending', value: 'score_desc' },
  { label: 'Patent ID: A → Z', value: 'patent_az' },
  { label: 'Patent ID: Z → A', value: 'patent_za' },
];

export const DEFAULT_PATENT_CARD_HEIGHT = 108;

export const DRAWER_MIN_WIDTH = 400;
export const DRAWER_MAX_WIDTH = 1200;
export const DRAWER_MIN_HEIGHT = 300;
export const DRAWER_MAX_HEIGHT = 800;

export const CONFERENCE_ID = 'conference-id';
